/** Auth context: the signed-in user, with login, logout and refresh over authApi and TokenManager. */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { TokenManager } from '@/lib/token-manager';
import { authApi } from '@/services/api';

interface AuthContextType {
  user: any | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: {
    username: string;
    password: string;
    two_fa_token?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState(TokenManager.getUser());
  const [isLoading, setIsLoading] = useState(true);
  const isAuthenticated = TokenManager.isTokenValid();

  /** Sign in; authApi stores the token hints. */
  const login = async (credentials: {
    username: string;
    password: string;
    two_fa_token?: string;
  }) => {
    const response = await authApi.login(credentials);
    // The authApi.login already handles token storage in TokenManager
    setUser(response.user);
  };

  /** Sign out. Local state is cleared even when the server call fails. */
  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      TokenManager.clearTokens();
      setUser(null);
    }
  };

  /**
   * Refresh user data from server
   */
  const refreshUser = async () => {
    try {
      // First, check if we have stored user data
      const storedUser = TokenManager.getUser();
      if (storedUser) {
        setUser(storedUser);
        return;
      }

      // If no stored user data but we have a valid token, fetch from server
      if (TokenManager.isTokenValid()) {
        console.log('Valid token but no user data found, fetching from server...');

        try {
          const currentUser = await authApi.getCurrentUser();
          // Store the user data and update state (now includes roles/permissions claims)
          TokenManager.updateUser(currentUser);
          setUser(currentUser);
        } catch (error) {
          console.error('Failed to fetch current user from server:', error);
          // If we can't fetch user data, the token might be invalid
          console.warn('Token appears to be invalid, logging out...');
          await logout();
        }
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      // If refresh fails and we're supposed to be authenticated, log out
      if (TokenManager.isTokenValid()) {
        await logout();
      }
    }
  };

  /**
   * Initialize authentication state
   */
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);

      try {
        // Check if we have a valid token
        if (TokenManager.isTokenValid()) {
          // Always attempt to refresh user data on init
          await refreshUser();
        } else {
          // Clear invalid tokens and reset state
          TokenManager.clearTokens();
          setUser(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        TokenManager.clearTokens();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Set up token refresh timer
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    // Check token expiration every minute
    const interval = setInterval(() => {
      if (TokenManager.isTokenValid()) {
        // Token is still valid, continue
        return;
      }

      // Token has expired, log out the user
      console.log('Token has expired, logging out...');
      logout();
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [isAuthenticated, logout]);

  const contextValue: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use authentication context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Higher-order component for protected routes
 */
interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string[];
  fallback?: ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole = [],
  fallback = <div>Access denied</div>
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // In a real app, you'd use React Router here
    window.location.href = '/signin';
    return null;
  }

  // Check role-based access
  if (requiredRole.length > 0 && user) {
    const hasRequiredRole = requiredRole.some(role =>
      user.role === role || user.roles?.includes(role)
    );

    if (!hasRequiredRole) {
      return fallback;
    }
  }

  return <>{children}</>;
};
